import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  User, BookMarked, FileEdit, Users, Settings, LogOut, Moon, Sun, ChevronDown,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { cx } from "@/components/site/primitives";
import { useAuth } from "@/lib/useAuth";
import { useTheme } from "@/hooks/use-theme";
import { useMediaQuery } from "@/hooks/use-media-query";
import { toast } from "@/components/ui/sonner";
import { LOGOUT } from "@/constants/testIds";

const spineTone = {
  coral: "from-coral to-[#b85e58]",
  yellow: "from-yellow to-[#e0b164]",
  sage: "from-sage to-[#a9c39d]",
  blue: "from-blue to-[#6b808b]",
};

const NAV_ITEMS = [
  { key: "profile", label: "Profile", icon: User, to: "/app/profile" },
  { key: "tbr", label: "TBR", icon: BookMarked, to: "/app/bookshelf?shelf=shelf_tbr" },
  { key: "drafts", label: "Drafts", icon: FileEdit, to: "/app/studio?tab=draft" },
  { key: "communities", label: "Communities", icon: Users, to: "/app/communities" },
  { key: "settings", label: "Settings", icon: Settings },
];

function Avatar({ user, className = "" }) {
  const displayName = user.displayName || user.name || user.username || user.email || "";

  return (
    <span className={cx("grid place-items-center rounded-full bg-gradient-to-br font-display font-bold text-[#FFF7F0] ring-2 ring-cream", spineTone[user.tone] || spineTone.coral, className)}>
      {user.initial || displayName.charAt(0).toUpperCase()}
    </span>
  );
}

function UserHeader({ user }) {
  const displayName = user.displayName || user.name || user.username || user.email || "";
  const stats = user.stats || {};

  return (
    <div className="flex items-center gap-3 px-2 py-2.5">
      <Avatar user={user} className="h-11 w-11 text-base shrink-0" />
      <div className="min-w-0 leading-tight">
        <p className="truncate font-display text-[15px] font-semibold text-ink">{displayName}</p>
        <p className="text-xs text-subtle">{user.tagline}</p>
        <p className="mt-1 text-[11px] font-medium text-subtle">
          <span className="text-coral">{stats.booksRead ?? 0}</span> books ·{" "}
          <span className="text-coral">{stats.writing ?? 0}</span> stories ·{" "}
          <span className="text-coral">{stats.streak ?? 0}</span>d streak
        </p>
      </div>
    </div>
  );
}

function DarkModeRow({ dark, toggle }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-md px-2 py-2 text-sm text-ink">
      <span className="flex items-center gap-2">
        {dark ? <Moon className="h-4 w-4 text-yellow" /> : <Sun className="h-4 w-4 text-coral" />}
        Dark Mode
      </span>
      <Switch
        checked={dark}
        onCheckedChange={toggle}
        aria-label="Toggle dark mode"
        data-testid="profile-menu-dark-mode"
        className="data-[state=checked]:bg-coral"
      />
    </div>
  );
}

export default function ProfileMenu() {
  const { user, signOut } = useAuth();
  const { dark, toggle } = useTheme();
  const navigate = useNavigate();
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const [open, setOpen] = useState(false);

  if (!user) return null;

  const handleNavItem = ({ label, to }) => {
    setOpen(false);
    if (to) {
      navigate(to);
      return;
    }
    toast(`${label} is coming soon in ScribbleReads.`);
  };

  const handleSignOut = async () => {
    setOpen(false);
    await signOut();
    toast("Signed out — see you soon!");
    navigate("/");
  };

  const trigger = (
    <button
      type="button"
      aria-label="Open profile menu"
      aria-haspopup="menu"
      data-testid="profile-menu-trigger"
      className="flex items-center gap-1.5 rounded-full border border-transparent py-1 pl-1 pr-2 transition-colors hover:border-line hover:bg-card"
    >
      <Avatar user={user} className="h-9 w-9 text-sm" />
      <ChevronDown className="hidden h-4 w-4 text-subtle md:block" />
    </button>
  );

  if (isDesktop) {
    return (
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          sideOffset={10}
          className="w-72 rounded-2xl border border-line bg-card p-2 text-ink shadow-xl"
        >
          <UserHeader user={user} />
          <DropdownMenuSeparator className="bg-line" />
          {NAV_ITEMS.map(({ key, label, icon: Icon, to }) => (
            <DropdownMenuItem
              key={key}
              onSelect={() => handleNavItem({ label, to })}
              className="cursor-pointer gap-2.5 rounded-lg px-2 py-2 text-sm text-ink focus:bg-sage/25 focus:text-ink"
            >
              <Icon className="h-4 w-4 text-subtle" /> {label}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator className="bg-line" />
          <DarkModeRow dark={dark} toggle={toggle} />
          <DropdownMenuSeparator className="bg-line" />
          <DropdownMenuItem
            onSelect={handleSignOut}
            data-testid={LOGOUT.button}
            className="cursor-pointer gap-2.5 rounded-lg px-2 py-2 text-sm font-medium text-coral focus:bg-coral/10 focus:text-coral"
          >
            <LogOut className="h-4 w-4" /> Sign Out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>{trigger}</SheetTrigger>
      <SheetContent side="bottom" className="rounded-t-3xl border-line bg-card p-4 pb-8 text-ink">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
          <div className="mx-auto mb-3 h-1.5 w-10 rounded-full bg-line" />
          <UserHeader user={user} />
          <div className="mt-2 border-t border-line pt-2">
            {NAV_ITEMS.map(({ key, label, icon: Icon, to }) => (
              <button
                key={key}
                onClick={() => handleNavItem({ label, to })}
                className="flex w-full items-center gap-3 rounded-xl px-2 py-3.5 text-left text-[15px] text-ink transition-colors hover:bg-sage/20"
              >
                <Icon className="h-[18px] w-[18px] text-subtle" /> {label}
              </button>
            ))}
          </div>
          <div className="border-t border-line pt-1">
            <div className="px-2 py-1">
              <DarkModeRow dark={dark} toggle={toggle} />
            </div>
          </div>
          <div className="mt-1 border-t border-line pt-2">
            <button
              onClick={handleSignOut}
              data-testid={LOGOUT.button}
              className="flex w-full items-center gap-3 rounded-xl px-2 py-3.5 text-left text-[15px] font-medium text-coral transition-colors hover:bg-coral/10"
            >
              <LogOut className="h-[18px] w-[18px]" /> Sign Out
            </button>
          </div>
        </motion.div>
      </SheetContent>
    </Sheet>
  );
}
